import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Dumbbell, Waves, Users, Gamepad2, CalendarClock, CheckCircle } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import styles from './Amenities.module.css';

const Amenities = () => {
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    date: '',
    timeSlot: 'Morning (06:00 AM - 09:00 AM)',
    guests: 1
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [amenitiesRes, bookingsRes] = await Promise.all([
        api.get('/community/amenities'),
        api.get('/users/amenity-bookings')
      ]);
      setAmenitiesList(amenitiesRes.data);
      setBookings(bookingsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch amenities data', error);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBookSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        amenityName: selectedAmenity.name,
        ...formData
      };
      const { data } = await api.post('/users/amenity-bookings', payload);
      setBookings([data, ...bookings]);
      setFormData({ date: '', timeSlot: 'Morning (06:00 AM - 09:00 AM)', guests: 1 });
      setSelectedAmenity(null);
      toast.success('Amenity booked successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to book amenity');
    }
  };

  const getIconForAmenity = (name) => {
    if (name.toLowerCase().includes('fitness') || name.toLowerCase().includes('gym')) return Dumbbell;
    if (name.toLowerCase().includes('pool')) return Waves;
    if (name.toLowerCase().includes('club')) return Users;
    if (name.toLowerCase().includes('game')) return Gamepad2;
    return Users;
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Society Amenities" 
        breadcrumb={['Services', 'Amenities & Bookings']} 
      />

      {bookings.length > 0 && (
        <div className={styles.bookingsSection}>
          <h2>My Active Bookings</h2>
          <div className={styles.bookingGrid}>
            {bookings.map(bk => (
              <div key={bk._id} className={styles.bookingCard}>
                <div className={styles.bkHeader}>
                  <strong>{bk.amenity}</strong>
                  <span className={styles.bkBadge}><CheckCircle size={12} /> {bk.status}</span>
                </div>
                <div className={styles.bkBody}>
                  <span>{new Date(bk.date).toLocaleDateString('en-GB')}</span>
                  <span>{bk.timeSlot}</span>
                  <span>{bk.guests} Guest(s)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.grid}>
        {loading ? <p>Loading amenities...</p> : amenitiesList.map(amenity => {
          const Icon = getIconForAmenity(amenity.name);
          const isClosed = amenity.status === 'Maintenance';
          return (
            <div key={amenity._id} className={`${styles.card} ${isClosed ? styles.closed : ''}`}>
              <div className={styles.iconWrapper}>
                <Icon size={28} />
              </div>
              <div className={styles.cardInfo}>
                <h3>{amenity.name}</h3>
                <span className={styles.capacity}>{amenity.capacity} people • {amenity.rules}</span>
              </div>
              <button 
                className={styles.bookBtn} 
                disabled={isClosed}
                onClick={() => setSelectedAmenity(amenity)}
              >
                {isClosed ? 'Closed for Maintenance' : 'Book Slot'}
              </button>
            </div>
          );
        })}
      </div>

      <Drawer 
        isOpen={!!selectedAmenity} 
        onClose={() => setSelectedAmenity(null)} 
        title={`Book ${selectedAmenity?.name}`}
      >
        {selectedAmenity && (
          <form className="drawerForm" onSubmit={handleBookSubmit}>
            <div className={styles.amenitySummary}>
              <CalendarClock size={24} color="#154530" />
              <p>Please select your preferred time slot. Max capacity is {selectedAmenity.capacity}.</p>
            </div>

            <div className="formGroup">
              <label>Select Date</label>
              <input type="date" name="date" value={formData.date} min={new Date().toISOString().split('T')[0]} onChange={handleChange} required />
            </div>
            <div className="formGroup">
              <label>Time Slot</label>
              <select name="timeSlot" value={formData.timeSlot} onChange={handleChange}>
                <option>Morning (06:00 AM - 09:00 AM)</option>
                <option>Afternoon (12:00 PM - 03:00 PM)</option>
                <option>Evening (05:00 PM - 08:00 PM)</option>
                <option>Night (08:00 PM - 10:00 PM)</option>
              </select>
            </div>
            <div className="formGroup">
              <label>Number of Guests (Including yourself)</label>
              <input type="number" name="guests" min="1" max="10" value={formData.guests} onChange={handleChange} required />
            </div>
            
            <div className="formActions">
              <button type="button" className="btnCancel" onClick={() => setSelectedAmenity(null)}>Cancel</button>
              <button type="submit" className="btnSubmit">Confirm Booking</button>
            </div>
          </form>
        )}
      </Drawer>
    </div>
  );
};

export default Amenities;
